/**
 * ═══════════════════════════════════════════════════════════════
 *  HomeBak — 已废弃 | 纯 Mock 原型 (一级企业首页看板)
 *
 *  当前线上版本见: src/pages/Home/HomePrimary.js
 *  本文件仅为归档参考，所有数据均为硬编码 Mock。
 *  如需恢复使用，请对接以下 API 模块:
 *    - 账务统计:  参照 docs/business/11-finance.md
 *    - 设备统计:  参照 docs/business/06-device.md
 *    - 订单/用户: 参照 docs/business/09-order.md
 * ═══════════════════════════════════════════════════════════════
 */
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Typography, Statistic, Space, Tabs, Tooltip } from 'antd';
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    ExclamationCircleOutlined,
    AccountBookOutlined,
    HddOutlined,
    CloudServerOutlined
} from '@ant-design/icons';
import * as echarts from 'echarts';

const { Title, Text } = Typography;

// ══════════════════════════════════════════════
//  通用辅助组件
// ══════════════════════════════════════════════

// 涨跌标签
const RateTag = ({ value, label = '同比' }) => {
    if (!value) return null;
    const isUp = value > 0;
    return (
        <span style={{ fontSize: 12, marginLeft: 4 }}>
            <Text type="secondary">{label}</Text>
            <span style={{ color: isUp ? '#cf1322' : '#3f8600', marginLeft: 4 }}>
                {isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                {Math.abs(value)}%
            </span>
        </span>
    );
};

// 基础统计卡片
const StatCard = ({ title, value, valueUsd, suffix, prefix, precision, subValue, rate, rateUsd, tooltip, color = '#262626' }) => (
    <Card bordered={false} className="stat-card" style={{ borderRadius: 12, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
                {title} {tooltip && <Tooltip title={tooltip}><ExclamationCircleOutlined style={{ fontSize: 12, marginLeft: 4, color: '#faad14' }} /></Tooltip>}
            </Text>
        </div>
        <div style={{ marginBottom: valueUsd !== undefined ? 4 : 8, display: 'flex', alignItems: 'baseline' }}>
            <span style={{ fontSize: 24, fontWeight: 600, color: color }}>
                {prefix && <span style={{ fontSize: 16, marginRight: 4 }}>{prefix}</span>}
                {typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision }) : value}
                {suffix && <span style={{ fontSize: 14, marginLeft: 4, fontWeight: 400 }}>{suffix}</span>}
            </span>
            {rate !== undefined && <RateTag value={rate} />}
        </div>
        {valueUsd !== undefined && (
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'baseline' }}>
                <span style={{ fontSize: 16, fontWeight: 500, color: color, opacity: 0.85 }}>
                    <span style={{ fontSize: 14, marginRight: 2 }}>$</span>
                    {typeof valueUsd === 'number' ? valueUsd.toLocaleString(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision }) : valueUsd}
                </span>
                {rateUsd !== undefined && <RateTag value={rateUsd} />}
            </div>
        )}
        {subValue && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Text style={{ fontSize: 12, marginRight: 12 }}>{subValue}</Text>
            </div>
        )}
    </Card>
);

// ══════════════════════════════════════════════
//  图表组件 (SVG实现)
// ══════════════════════════════════════════════

// 1. 收支趋势混合图表 (ECharts)
const IncomeExpenseTrendChart = ({ data, height = 300 }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current || !data || data.length === 0) return;

        const chart = echarts.init(chartRef.current);
        const option = {
            tooltip: { trigger: 'axis' },
            legend: {
                data: ['收入(¥)', '收入($)', '支出(¥)', '支出($)'],
                top: 10
            },
            grid: { left: 60, right: 30, top: 50, bottom: 30, containLabel: true },
            xAxis: {
                type: 'category',
                data: data.map(d => d.label),
                boundaryGap: false
            },
            yAxis: { type: 'value' },
            series: [
                {
                    name: '收入(¥)',
                    type: 'line',
                    data: data.map(d => d.income_cny),
                    itemStyle: { color: '#52c41a' },
                    lineStyle: { color: '#52c41a' },
                    smooth: true
                },
                {
                    name: '收入($)',
                    type: 'line',
                    data: data.map(d => d.income_usd),
                    itemStyle: { color: '#95de64' },
                    lineStyle: { color: '#95de64' },
                    smooth: true
                },
                {
                    name: '支出(¥)',
                    type: 'bar',
                    data: data.map(d => d.expense_cny),
                    itemStyle: { color: '#f5222d' }
                },
                {
                    name: '支出($)',
                    type: 'bar',
                    data: data.map(d => d.expense_usd),
                    itemStyle: { color: '#ff7875' }
                }
            ]
        };

        chart.setOption(option);
        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, [data]);

    return <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: height }} />;
};

// 2. 单线趋势图 (ECharts)
const SimpleLineChart = ({ data, color = '#52c41a', height = 200 }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current || !data || data.length === 0) return;

        const chart = echarts.init(chartRef.current);
        const option = {
            tooltip: { trigger: 'axis' },
            grid: { left: 60, right: 30, top: 20, bottom: 40, containLabel: true },
            xAxis: {
                type: 'category',
                data: data.map(d => d.label),
                boundaryGap: false
            },
            yAxis: { type: 'value' },
            series: [
                {
                    type: 'line',
                    data: data.map(d => d.value),
                    itemStyle: { color },
                    lineStyle: { color },
                    areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: color + '40' },
                        { offset: 1, color: color + '00' }
                    ]) },
                    smooth: true
                }
            ]
        };

        chart.setOption(option);
        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.dispose();
        };
    }, [data, color]);

    return <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: height }} />;
};

// 2. 堆叠柱状图 (用于订单收益趋势)
const StackedBarChart = ({ data, colors = ['#1890ff', '#2fc25b'], height = 200 }) => {
    const max = Math.max(...data.map(d => d.v1 + d.v2)) || 1;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 8, borderBottom: '1px solid #f0f0f0', paddingBottom: 4 }}>
                {data.map((d, i) => {
                    const h1 = (d.v1 / max) * 100;
                    const h2 = (d.v2 / max) * 100;
                    return (
                        <div key={i} style={{ flex: 1, height: '100%', cursor: 'pointer' }}>
                            <Tooltip title={`${d.label}: 订阅 ¥${d.v1.toLocaleString()} ($${(d.v1_usd || 0).toLocaleString()}), 非订阅 ¥${d.v2.toLocaleString()} ($${(d.v2_usd || 0).toLocaleString()})`}>
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column-reverse' }}>
                                    <div style={{ width: '100%', background: colors[0], height: `${h1}%`, borderRadius: h2 === 0 ? '2px 2px 0 0' : 0 }} />
                                    <div style={{ width: '100%', background: colors[1], height: `${h2}%`, borderRadius: '2px 2px 0 0' }} />
                                </div>
                            </Tooltip>
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                {data.map((d, i) => (
                    <Text key={i} type="secondary" style={{ fontSize: 10, flex: 1, textAlign: 'center' }}>{d.label}</Text>
                ))}
            </div>
        </div>
    );
};

// 3. 双柱状图 + 折线 (用于用户付费趋势)
const MultiChart = ({ data, barColors = ['#1890ff', '#85ccff'], lineColor = '#faad14', height = 200 }) => {
    const maxBar = Math.max(...data.map(d => Math.max(d.b1, d.b2))) || 1;
    const maxLine = Math.max(...data.map(d => d.line)) || 1;
    return (
        <div style={{ position: 'relative', height: height + 30 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', height, gap: 8, borderBottom: '1px solid #f0f0f0', paddingBottom: 4 }}>
                {data.map((d, i) => {
                    const h1 = (d.b1 / maxBar) * 100;
                    const h2 = (d.b2 / maxBar) * 100;
                    return (
                        <div key={i} style={{ flex: 1, height: '100%', cursor: 'pointer' }}>
                            <Tooltip title={`${d.label}: 付费用户 ${d.b1}, 新增付费 ${d.b2}`}>
                                <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                                    <div style={{ width: '40%', background: barColors[0], height: `${h1}%`, borderRadius: '2px 2px 0 0' }} />
                                    <div style={{ width: '40%', background: barColors[1], height: `${h2}%`, borderRadius: '2px 2px 0 0' }} />
                                </div>
                            </Tooltip>
                        </div>
                    );
                })}
            </div>
            {/* 折线叠加 */}
            <svg width="100%" height={height} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}>
                {(() => {
                    const points = data.map((d, i) => {
                        const x = (i / (data.length - 1)) * 100;
                        const y = height - (d.line / maxLine) * (height - 40) - 20;
                        return `${x}%,${y}`;
                    }).join(' ');
                    return <polyline points={points} fill="none" stroke={lineColor} strokeWidth="1.5" />;
                })()}
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                {data.map((d, i) => (
                    <Text key={i} type="secondary" style={{ fontSize: 10, flex: 1, textAlign: 'center' }}>{d.label}</Text>
                ))}
            </div>
        </div>
    );
};

// 4. 漏斗图
const FunnelChart = ({ data, title }) => {
    const maxWidth = 200;
    const baseHeight = 40;
    return (
        <div style={{ textAlign: 'center' }}>
            <Text strong style={{ display: 'block', marginBottom: 16 }}>{title}</Text>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {data.map((d, i) => {
                    const width = (d.value / data[0].value) * maxWidth;
                    const nextWidth = data[i+1] ? (data[i+1].value / data[0].value) * maxWidth : width * 0.6;
                    return (
                        <div key={i} style={{ position: 'relative', marginBottom: 4 }}>
                            <div style={{ 
                                width, 
                                height: baseHeight, 
                                background: d.color, 
                                clipPath: `polygon(0 0, 100% 0, ${50 + (nextWidth/width)*50}% 100%, ${50 - (nextWidth/width)*50}% 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 500,
                                transition: 'all 0.3s'
                            }}>
                                {d.label}
                            </div>
                            <div style={{ marginTop: 2 }}>
                                <Text strong style={{ fontSize: 14 }}>{d.value.toLocaleString()}</Text>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// 图例标记组件
const Badge = ({ color, text }) => (
    <span style={{ fontSize: 12, display: 'flex', alignItems: 'center' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 4 }} />
        {text}
    </span>
);

// ══════════════════════════════════════════════
//  Mock Data
// ══════════════════════════════════════════════

const MOCK_FINANCE = {
    yesterday: {
        expense_commission: 1280.50,
        expense_commission_usd: 178.50,
        expense_share: 850.20,
        expense_share_usd: 118.60,
        expense_consume: 500.00,
        expense_consume_usd: 69.80,
        income_value_added: 3500.00,
        income_value_added_usd: 488.10,
        income_share: 1200.00,
        income_share_usd: 167.30,
        rates: {
            expense_commission: 5.2,
            expense_commission_usd: 5.0,
            expense_share: -2.1,
            expense_share_usd: -2.3,
            expense_consume: 10.5,
            expense_consume_usd: 10.2,
            income_value_added: 8.4,
            income_value_added_usd: 8.1,
            income_share: 3.2,
            income_share_usd: 3.0
        }
    },
    month: {
        expense_commission: 45000.00,
        expense_commission_usd: 6276.10,
        expense_share: 28000.00,
        expense_share_usd: 3905.10,
        expense_consume: 15000.00,
        expense_consume_usd: 2092.00,
        income_value_added: 125000.00,
        income_value_added_usd: 17433.80,
        income_share: 42000.00,
        income_share_usd: 5857.70,
        rates: {
            expense_commission: 12.5,
            expense_commission_usd: 12.2,
            expense_share: 5.8,
            expense_share_usd: 5.5,
            expense_consume: 15.2,
            expense_consume_usd: 14.8,
            income_value_added: 18.4,
            income_value_added_usd: 18.1,
            income_share: 10.1,
            income_share_usd: 9.8
        }
    },
    trend: [
        { label: '05-01', income_cny: 4200, income_usd: 585, expense_cny: 2100, expense_usd: 292 },
        { label: '05-02', income_cny: 3800, income_usd: 530, expense_cny: 1900, expense_usd: 265 },
        { label: '05-03', income_cny: 4500, income_usd: 627, expense_cny: 2250, expense_usd: 314 },
        { label: '05-04', income_cny: 5100, income_usd: 711, expense_cny: 2550, expense_usd: 355 },
        { label: '05-05', income_cny: 4800, income_usd: 669, expense_cny: 2400, expense_usd: 334 },
        { label: '05-06', income_cny: 5500, income_usd: 766, expense_cny: 2750, expense_usd: 383 },
        { label: '05-07', income_cny: 6200, income_usd: 864, expense_cny: 3100, expense_usd: 432 },
    ]
};

const MOCK_DEVICE = {
    license_total: 150000,
    produce_qty: 128400,
    activated_qty: 110300,
    activated_rate: 85.9,
    active_qty: 98500,
    active_rate: 76.7,
    online_qty: 65400,
    retention_rate: 66.4,
    trend: [
        { label: '05-01', value: 58000 },
        { label: '05-02', value: 59500 },
        { label: '05-03', value: 61000 },
        { label: '05-04', value: 60500 },
        { label: '05-05', value: 62000 },
        { label: '05-06', value: 64000 },
        { label: '05-07', value: 65400 },
    ]
};

const MOCK_APP = {
    users: {
        total: 1254000,
        yesterday_active: 85400,
        yesterday_new: 3200
    },
    orders: {
        today: {
            count: 1250,
            amount: 32500,
            amount_usd: 4540.00,
            users: 1100,
            rates: { count: 12.5, amount: 15.4, amount_usd: 14.8, users: 10.2 }
        },
        month: {
            count: 38500,
            amount: 985000,
            amount_usd: 137350.00,
            users: 32000,
            rates: { count: 18.2, amount: 22.1, amount_usd: 21.5, users: 15.5 }
        }
    },
    revenue_trend: [
        { label: '05-01', v1: 12000, v1_usd: 1675, v2: 8000, v2_usd: 1115 },
        { label: '05-02', v1: 15000, v1_usd: 2095, v2: 7000, v2_usd: 975 },
        { label: '05-03', v1: 11000, v1_usd: 1535, v2: 9000, v2_usd: 1255 },
        { label: '05-04', v1: 18000, v1_usd: 2515, v2: 6000, v2_usd: 835 },
        { label: '05-05', v1: 16000, v1_usd: 2235, v2: 10000, v2_usd: 1395 },
        { label: '05-06', v1: 20000, v1_usd: 2795, v2: 5000, v2_usd: 695 },
        { label: '05-07', v1: 22000, v1_usd: 3075, v2: 8000, v2_usd: 1115 },
    ],
    pay_trend: [
        { label: '05-01', b1: 8500, b2: 120, line: 8.5 },
        { label: '05-02', b1: 9200, b2: 150, line: 9.1 },
        { label: '05-03', b1: 8800, b2: 110, line: 8.7 },
        { label: '05-04', b1: 10500, b2: 200, line: 10.2 },
        { label: '05-05', b1: 9800, b2: 180, line: 9.6 },
        { label: '05-06', b1: 11200, b2: 250, line: 11.5 },
        { label: '05-07', b1: 12500, b2: 300, line: 12.4 },
        { label: '05-08', b1: 11800, b2: 280, line: 11.2 },
        { label: '05-09', b1: 13000, b2: 320, line: 13.1 },
        { label: '05-10', b1: 14500, b2: 350, line: 14.5 },
    ],
    funnel: {
        this_month: [
            { label: '购买页面访问量', value: 12860, color: '#1890ff' },
            { label: '订单下单量', value: 8240, color: '#36cfc9' },
            { label: '付款量', value: 6180, color: '#52c41a' },
            { label: '订阅量', value: 4862, color: '#f5222d' },
        ],
        last_month: [
            { label: '购买页面访问量', value: 10520, color: '#d9d9d9' },
            { label: '订单下单量', value: 6680, color: '#d9d9d9' },
            { label: '付款量', value: 4750, color: '#d9d9d9' },
            { label: '订阅量', value: 3526, color: '#d9d9d9' },
        ]
    },
    active_trend: [
        { label: '04-13', value: 412, last: 380 },
        { label: '04-14', value: 398, last: 390 },
        { label: '04-15', value: 456, last: 400 },
        { label: '04-16', value: 482, last: 420 },
        { label: '04-17', value: 501, last: 440 },
        { label: '04-18', value: 468, last: 410 },
        { label: '04-19', value: 438, last: 395 },
    ]
};

// ══════════════════════════════════════════════
//  主组件
// ══════════════════════════════════════════════

const HomeBak = () => {
    const [orderPeriod, setOrderPeriod] = useState('today');

    return (
        <div style={{ padding: '24px', background: '#fff', minHeight: '100%' }}>

            {/* ═══ 昨日数据统计 ═══ */}
            <Title level={3} style={{ marginBottom: 24 }}>昨日数据统计</Title>
            
            {/* ═══ 账务信息 ═══ */}
            <div style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>
                        <AccountBookOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        账务统计
                    </Title>
                </div>
                <Row gutter={[16, 16]}>
                    <Col span={16}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <StatCard
                                    title="App云存收益"
                                    prefix="¥"
                                    value={MOCK_FINANCE.yesterday.income_value_added}
                                    valueUsd={MOCK_FINANCE.yesterday.income_value_added_usd}
                                    precision={2}
                                    rate={MOCK_FINANCE.yesterday.rates.income_value_added}
                                    rateUsd={MOCK_FINANCE.yesterday.rates.income_value_added_usd}
                                    color="#52c41a"
                                />
                            </Col>
                            <Col span={12}>
                                <StatCard
                                    title="分润收益(预估)"
                                    prefix="¥"
                                    value={MOCK_FINANCE.yesterday.income_share}
                                    valueUsd={MOCK_FINANCE.yesterday.income_share_usd}
                                    precision={2}
                                    rate={MOCK_FINANCE.yesterday.rates.income_share}
                                    rateUsd={MOCK_FINANCE.yesterday.rates.income_share_usd}
                                    color="#52c41a"
                                />
                            </Col>
                            <Col span={8}>
                                <StatCard
                                    title="平台抽佣(预估)"
                                    prefix="¥"
                                    value={MOCK_FINANCE.yesterday.expense_commission}
                                    valueUsd={MOCK_FINANCE.yesterday.expense_commission_usd}
                                    precision={2}
                                    rate={MOCK_FINANCE.yesterday.rates.expense_commission}
                                    rateUsd={MOCK_FINANCE.yesterday.rates.expense_commission_usd}
                                    color="#f5222d"
                                />
                            </Col>
                            <Col span={8}>
                                <StatCard
                                    title="分润支出(预估)"
                                    prefix="¥"
                                    value={MOCK_FINANCE.yesterday.expense_share}
                                    valueUsd={MOCK_FINANCE.yesterday.expense_share_usd}
                                    precision={2}
                                    rate={MOCK_FINANCE.yesterday.rates.expense_share}
                                    rateUsd={MOCK_FINANCE.yesterday.rates.expense_share_usd}
                                    color="#f5222d"
                                />
                            </Col>
                            <Col span={8}>
                                <StatCard
                                    title="平台消费"
                                    prefix="¥"
                                    value={MOCK_FINANCE.yesterday.expense_consume}
                                    valueUsd={MOCK_FINANCE.yesterday.expense_consume_usd}
                                    precision={2}
                                    rate={MOCK_FINANCE.yesterday.rates.expense_consume}
                                    rateUsd={MOCK_FINANCE.yesterday.rates.expense_consume_usd}
                                    color="#f5222d"
                                />
                            </Col>                        </Row>
                    </Col>
                    <Col span={8}>
                        <Card bordered={false} style={{ borderRadius: 12, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }} styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%' } }}>
                            <Text strong style={{ display: 'block', marginBottom: 16 }}>收支近7天趋势</Text>
                            <div style={{ flex: 1, minHeight: 300 }}>
                                <IncomeExpenseTrendChart data={MOCK_FINANCE.trend} height={300} />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* ═══ App运营 ═══ */}
            <div>
                <Title level={4} style={{ marginBottom: 16 }}>
                    <CloudServerOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                    App运营
                </Title>
                
                {/* 1. 用户与订单基础指标 */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <Card title="用户指标" bordered={false} style={{ borderRadius: 12, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <div><Text type="secondary">总用户数</Text> <div style={{ fontSize: 24, fontWeight: 600 }}>{MOCK_APP.users.total.toLocaleString()}</div></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div><Text type="secondary">昨日活跃</Text> <div style={{ fontSize: 18, fontWeight: 600 }}>{MOCK_APP.users.yesterday_active.toLocaleString()}</div></div>
                                    <div><Text type="secondary">昨日新增</Text> <div style={{ fontSize: 18, fontWeight: 600 }}>{MOCK_APP.users.yesterday_new.toLocaleString()}</div></div>
                                </div>
                            </Space>
                        </Card>
                    </Col>
                    <Col span={16}>
                        <Card 
                            title="订单指标" 
                            bordered={false} 
                            style={{ borderRadius: 12, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}
                            extra={
                                <Tabs
                                    activeKey={orderPeriod}
                                    onChange={setOrderPeriod}
                                    size="small"
                                    items={[
                                        { label: '今日', key: 'today' },
                                        { label: '当月', key: 'month' },
                                    ]}
                                    style={{ marginBottom: -16 }}
                                />
                            }
                        >
                            <Row gutter={24} align="middle" style={{ height: '100%', padding: '8px 0' }}>
                                <Col span={7} style={{ borderRight: '1px solid #f0f0f0' }}>
                                    <Statistic 
                                        title={<Text type="secondary" style={{ fontSize: 13 }}>订单总数</Text>} 
                                        value={MOCK_APP.orders[orderPeriod].count} 
                                        valueStyle={{ fontSize: 24, fontWeight: 600 }} 
                                        suffix={<RateTag value={MOCK_APP.orders[orderPeriod].rates.count} />}
                                    />
                                </Col>
                                <Col span={10} style={{ borderRight: '1px solid #f0f0f0', paddingLeft: 24 }}>
                                    <div style={{ marginBottom: 4 }}><Text type="secondary" style={{ fontSize: 13 }}>订单总额</Text></div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                            <Text strong style={{ fontSize: 24, color: '#f5222d' }}>
                                                ¥ {MOCK_APP.orders[orderPeriod].amount.toLocaleString()}
                                            </Text>
                                            <RateTag value={MOCK_APP.orders[orderPeriod].rates.amount} />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                            <Text style={{ fontSize: 16, color: 'rgba(0,0,0,0.45)', fontWeight: 500 }}>
                                                $ {MOCK_APP.orders[orderPeriod].amount_usd.toLocaleString()}
                                            </Text>
                                            <RateTag value={MOCK_APP.orders[orderPeriod].rates.amount_usd} />
                                        </div>
                                    </div>
                                </Col>
                                <Col span={7} style={{ paddingLeft: 24 }}>
                                    <Statistic 
                                        title={<Text type="secondary" style={{ fontSize: 13 }}>下单用户</Text>} 
                                        value={MOCK_APP.orders[orderPeriod].users} 
                                        valueStyle={{ fontSize: 24, fontWeight: 600 }} 
                                        suffix={<RateTag value={MOCK_APP.orders[orderPeriod].rates.users} />}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>

                {/* 2. 趋势分析 */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col span={12}>
                        <Card title="订单近7日收益" bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}
                            extra={<Space><Badge color="#1890ff" text="订阅" /><Badge color="#2fc25b" text="非订阅" /></Space>}>
                            <StackedBarChart data={MOCK_APP.revenue_trend} />
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="用户近7天付费" bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}
                            extra={<Space><Badge color="#1890ff" text="付费用户" /><Badge color="#85ccff" text="新增付费" /><Badge color="#faad14" text="付费率" /></Space>}>
                            <MultiChart data={MOCK_APP.pay_trend} />
                        </Card>
                    </Col>
                </Row>

                {/* 3. 用户转化分析 */}
                <Row gutter={[16, 16]}>
                    <Col span={16}>
                        <Card title="用户转化漏斗 (本月 vs 上月)" bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <Row>
                                <Col span={12} style={{ borderRight: '1px dotted #d9d9d9' }}>
                                    <FunnelChart title="本月 (05/01 ~ 05/07)" data={MOCK_APP.funnel.this_month} />
                                </Col>
                                <Col span={12}>
                                    <FunnelChart title="上月同期 (04/01 ~ 04/07)" data={MOCK_APP.funnel.last_month} />
                                </Col>
                            </Row>
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <Text type="secondary">整体转化率 <Text strong style={{ color: '#cf1322' }}>↑ +2.3pp</Text> vs 上月</Text>
                            </div>
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card title="用户活跃趋势" bordered={false} style={{ borderRadius: 12, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}
                            extra={<Space><Badge color="#00b96b" text="本周" /><Badge color="#d9d9d9" text="上周" /></Space>}>
                            <div style={{ position: 'relative', height: 260 }}>
                                <div style={{ display: 'flex', alignItems: 'flex-end', height: 200, gap: 4, borderBottom: '1px solid #f0f0f0', paddingBottom: 4 }}>
                                    {MOCK_APP.active_trend.map((d, i) => {
                                        const maxVal = Math.max(...MOCK_APP.active_trend.map(item => Math.max(item.value, item.last))) || 1;
                                        const h = (d.value / maxVal) * 100;
                                        return (
                                            <div key={i} style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                <div style={{ width: '100%', background: '#00b96b', height: `${h}%`, borderRadius: '2px 2px 0 0' }} />
                                            </div>
                                        );
                                    })}
                                </div>
                                <svg width="100%" height={200} viewBox="0 0 100 200" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}>
                                    {(() => {
                                        const maxVal = Math.max(...MOCK_APP.active_trend.map(item => Math.max(item.value, item.last))) || 1;
                                        const points = MOCK_APP.active_trend.map((d, i) => {
                                            const x = (i / (MOCK_APP.active_trend.length - 1)) * 100;
                                            const y = 200 - (d.last / maxVal) * 200;
                                            return `${x},${y}`;
                                        }).join(' ');
                                        return (
                                            <>
                                                <polyline points={points} fill="none" stroke="#d9d9d9" strokeWidth="1.5" strokeDasharray="3,3" vectorEffect="non-scaling-stroke" />
                                                {MOCK_APP.active_trend.map((d, i) => {
                                                    const x = (i / (MOCK_APP.active_trend.length - 1)) * 100;
                                                    const y = 200 - (d.last / maxVal) * 200;
                                                    return <circle key={i} cx={x} cy={y} r="2" fill="#fff" stroke="#d9d9d9" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />;
                                                })}
                                            </>
                                        );
                                    })()}
                                </svg>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                                    {MOCK_APP.active_trend.map((d, i) => (
                                        <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                            <div style={{ fontSize: 9, color: '#999' }}>{d.label.split('-')[1]}</div>
                                            <div style={{ fontSize: 9, color: '#52c41a' }}>▲{( (d.value/d.last - 1) * 100 ).toFixed(1)}%</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* ═══ 设备信息 ═══ */}
            <div style={{ marginBottom: 32, marginTop: 48 }}>
                <Title level={4} style={{ marginBottom: 16 }}>
                    <HddOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                    设备统计
                </Title>
                <Row gutter={[16, 16]}>
                    <Col span={5}>
                        <StatCard title="License总数" value={MOCK_DEVICE.license_total} suffix="台"
                            tooltip="企业授权码的总数，即所有License汇总" />
                    </Col>
                    <Col span={5}>
                        <StatCard title="生产数量" value={MOCK_DEVICE.produce_qty} suffix="台"
                            tooltip="使用平台产测工具，License烧录到设备中 + 非平台产测的License（因为未上报烧录状态，所以工单按已生产统计）" />
                    </Col>
                    <Col span={5}>
                        <StatCard
                            title="激活数量"
                            value={MOCK_DEVICE.activated_qty}
                            suffix="台"
                            subValue={`激活率: ${MOCK_DEVICE.activated_rate}%`}
                        />
                    </Col>
                    <Col span={5}>
                        <StatCard
                            title="绑定数量"
                            value={MOCK_DEVICE.active_qty}
                            suffix="台"
                            subValue={`绑定率: ${MOCK_DEVICE.active_rate}%`}
                        />
                    </Col>
                    <Col span={4}>
                        <StatCard
                            title="在线设备"
                            value={MOCK_DEVICE.online_qty}
                            suffix="台"
                            subValue={`留存率: ${MOCK_DEVICE.retention_rate}%`}
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col span={24}>
                        <Card bordered={false} style={{ borderRadius: 12, height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }} styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%' } }}>
                            <Text strong style={{ display: 'block', marginBottom: 16 }}>近7日在线设备趋势</Text>
                            <div style={{ flex: 1, minHeight: 250 }}>
                                <SimpleLineChart data={MOCK_DEVICE.trend} color="#52c41a" height={250} />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>

            <style>{`
                .stat-card:hover {
                    transform: translateY(-4px);
                    transition: all 0.3s;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
                }
            `}</style>
        </div>
    );
};

export default HomeBak;
